"use client";

import { useState } from "react";
import EyeIcon from "@/assets/images/eye.svg";
import EyeOffIcon from "@/assets/images/eye-off.svg";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

interface Props {
  id: string;
  label: string;
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  required?: boolean;
  minLength?: number;
  autoComplete?: string;
  disabled?: boolean;
}

/**
 * PasswordField — password input with a show/hide eye toggle.
 * Feature-local to the forgot-password flow.
 */
const PasswordField: React.FC<Props> = ({
  id,
  label,
  value,
  onChange,
  placeholder,
  required,
  minLength,
  autoComplete = "new-password",
  disabled,
}) => {
  const [show, setShow] = useState(false);

  return (
    <div className="flex flex-col gap-1.5">
      <Label htmlFor={id} className="text-sm text-text-primary">
        {label}
      </Label>
      <div className="relative">
        <Input
          id={id}
          type={show ? "text" : "password"}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          required={required}
          minLength={minLength}
          autoComplete={autoComplete}
          disabled={disabled}
          className="pr-10 placeholder:text-text-tertiary"
        />
        <button
          type="button"
          onClick={() => setShow((s) => !s)}
          className="absolute inset-y-0 right-0 flex items-center pr-3 text-text-tertiary transition-colors hover:text-text-primary disabled:opacity-50"
          tabIndex={-1}
          disabled={disabled}
          aria-label={show ? "Hide password" : "Show password"}
        >
          {show ? (
            <EyeIcon className="size-5 [&_*]:stroke-current" />
          ) : (
            <EyeOffIcon className="size-5 [&_*]:stroke-current" />
          )}
        </button>
      </div>
    </div>
  );
};

export default PasswordField;
